import { Fragment, useEffect, useState } from 'react';
import { Avatar, Button, Modal, Spin, Tag, Typography } from 'antd';
import { CheckOutlined, RightOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFormGroups } from '../../services/addFormApi';
import DescriptionBlock from '../detail/DescriptionBlock';

const { Text } = Typography;

function getInitials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
}

function getRecordName(record) {
  if (!record) return '';
  if (record.name) return record.name;
  if (record.fullName) return record.fullName;
  if (record.firstName || record.lastName) return [record.firstName, record.lastName].filter(Boolean).join(' ');
  return record.candidateName ?? record.clientName ?? record.jobTitle ?? '';
}

function getRecordEmail(record) {
  if (!record) return '';
  return record.email ?? record.emailId ?? record.emailAddress ?? '';
}

function getRecordStatus(record) {
  if (!record) return '';
  if (record.status) return record.status;
  if (record.stage) return record.stage;
  if (typeof record.isActive === 'boolean') return record.isActive ? 'Active' : 'Inactive';
  return '';
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return value.label ?? value.name ?? Object.values(value).join(' ');
  return String(value);
}

const DESCRIPTION_KEYS = ['jobDescription', 'description', 'rawDescription', 'jobDescriptionText'];

function isJobModule(moduleName) {
  return String(moduleName ?? '').toLowerCase() === 'job';
}

function isDescriptionField(field) {
  const matchesKey = DESCRIPTION_KEYS.includes(field.field);
  const matchesLabel = /description/i.test(field.label ?? '');
  return matchesKey || matchesLabel;
}

function WizardStepper({ steps, current, onStepClick }) {
  return (
    <div className="preview-stepper">
      {steps.map((step, index) => {
        const isDone = index < current;
        const isActive = index === current;
        const isLast = index === steps.length - 1;

        return (
          <Fragment key={step.title ?? index}>
            <button
              type="button"
              className={`preview-stepper__item${isActive ? ' preview-stepper__item--active' : ''}`}
              onClick={() => onStepClick(index)}
              aria-label={`Go to step ${index + 1}: ${step.title}`}
              aria-current={isActive ? 'step' : undefined}
            >
              <div
                className={[
                  'preview-stepper__circle',
                  isDone ? 'preview-stepper__circle--done' : '',
                  isActive ? 'preview-stepper__circle--active' : '',
                ].filter(Boolean).join(' ')}
              >
                {isDone ? <CheckOutlined /> : index + 1}
              </div>
              <span className={`preview-stepper__label${isActive ? ' preview-stepper__label--active' : ''}`}>
                {step.title}
              </span>
            </button>
            {!isLast && (
              <div className={`preview-stepper__line${isDone ? ' preview-stepper__line--done' : ''}`} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

function ProfileCard({ record }) {
  const name = getRecordName(record);
  const email = getRecordEmail(record);
  const status = getRecordStatus(record);

  if (!name && !email) return null;

  return (
    <div className="preview-profile-card">
      <div className="preview-profile-card__info">
        <Avatar className="preview-profile-card__avatar">{getInitials(name)}</Avatar>
        <div className="preview-profile-card__text">
          {name && <span className="preview-profile-card__name">{name}</span>}
          {email && <span className="preview-profile-card__email">{email}</span>}
        </div>
      </div>
      {status && <Tag className="preview-profile-card__status">{status}</Tag>}
    </div>
  );
}

function FieldsDisplay({ fields, moduleName }) {
  if (!fields?.length) return null;

  return (
    <div className="preview-field-grid">
      {fields.map((field) => {
        const raw = field.value;
        const isArray = Array.isArray(raw) && raw.length > 0;
        const showFormattedDescription = isJobModule(moduleName)
          && isDescriptionField(field)
          && typeof raw === 'string'
          && raw.trim();

        if (isArray) {
          return raw.map((item, idx) => (
            <div key={`${field.field ?? field.label}-${idx}`} className="preview-field-card">
              <span className="preview-field-card__label">
                {field.label}{raw.length > 1 ? ` (${idx + 1})` : ''}
              </span>
              <span className="preview-field-card__value">{formatValue(item)}</span>
            </div>
          ));
        }

        if (showFormattedDescription) {
          return (
            <div key={field.field ?? field.label} className="preview-field-card preview-field-card--description">
              <span className="preview-field-card__label">{field.label}</span>
              <div className="preview-field-card__description">
                <DescriptionBlock raw={raw} />
              </div>
            </div>
          );
        }

        return (
          <div key={field.field ?? field.label} className="preview-field-card">
            <span className="preview-field-card__label">{field.label}</span>
            <span className="preview-field-card__value">{formatValue(raw)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function PreviewPopup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { moduleName = '', record = null } = location.state ?? {};

  const id = record?.id ?? record?._id;
  const [formGroups, setFormGroups] = useState([]);
  const [loading, setLoading] = useState(Boolean(moduleName && id));
  const [error, setError] = useState(null);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!moduleName || !id) return;

    let cancelled = false;

    getFormGroups({ module: moduleName, id })
      .then((data) => {
        if (!cancelled) {
          setFormGroups(Array.isArray(data) ? data : []);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load preview');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [moduleName, id]);

  function handleClose() { navigate(-1); }

  const stepCount = formGroups.length;
  const hasMultipleSteps = stepCount > 1;
  const currentGroup = formGroups[current];

  const modalWidth = stepCount > 4
    ? `min(${stepCount * 105 + 80}px, min(95vw, 1200px))`
    : 'min(680px, calc(100vw - 32px))';

  const stepsItems = formGroups.map((group, index) => ({
    title: group.label ?? group.name ?? `Step ${index + 1}`,
  }));

  const modalTitle = (
    <div className="preview-modal-header">
      <span className="preview-modal-module">{moduleName.toUpperCase()}</span>
      <span className="preview-modal-title">
        {currentGroup?.label ?? (moduleName ? `Preview - ${moduleName}` : 'Preview')}
      </span>
    </div>
  );

  const footer = (
    <div className="wizard-footer">
      {hasMultipleSteps && (
        <Text className="preview-footer-step-count">Step {current + 1} of {stepCount}</Text>
      )}
      <div className="wizard-footer-nav">
        <Button onClick={handleClose}>Close</Button>
        {hasMultipleSteps && current > 0 && (
          <Button onClick={() => setCurrent((p) => p - 1)}>Back</Button>
        )}
        {hasMultipleSteps && (
          current < stepCount - 1 ? (
            <Button
              type="primary"
              icon={<RightOutlined />}
              iconPosition="end"
              onClick={() => setCurrent((p) => p + 1)}
            >
              Next
            </Button>
          ) : (
            <Button type="primary" onClick={handleClose}>Done</Button>
          )
        )}
      </div>
    </div>
  );

  function renderBody() {
    if (!moduleName || !id) {
      return <div className="job-preview-content job-preview-status">Preview id is missing.</div>;
    }
    if (loading) {
      return <div className="job-preview-content job-preview-status"><Spin size="small" /></div>;
    }
    if (error) {
      return <div className="job-preview-content job-preview-status job-preview-error">{error}</div>;
    }
    if (!formGroups.length) {
      return <div className="job-preview-content job-preview-status">No form groups found.</div>;
    }

    return formGroups.map((group, index) => (
      <div key={group.name ?? group.id ?? index} style={{ display: index === current ? 'block' : 'none' }}>
        {group.fields?.length > 0 ? (
          <FieldsDisplay fields={group.fields} moduleName={moduleName} />
        ) : (
          <Text type="secondary">No fields in this group.</Text>
        )}
      </div>
    ));
  }

  return (
    <Modal
      centered
      className="wizard-modal preview-modal"
      footer={footer}
      open
      title={modalTitle}
      width={modalWidth}
      onCancel={handleClose}
      maskClosable={false}
    >
      {hasMultipleSteps && (
        <div className="wizard-steps-container">
          <WizardStepper steps={stepsItems} current={current} onStepClick={setCurrent} />
        </div>
      )}
      <div className="wizard-body">
        {record && <ProfileCard record={record} />}
        {renderBody()}
      </div>
    </Modal>
  );
}
